import React, { forwardRef, useRef, isValidElement, useImperativeHandle } from 'react';
import { Loader } from 'react-feather';
import { useMergeValue } from '../../hooks/useMergeValue.js';
import { isSelectOption, isSelectOptGroup } from '../select/utils/selectHelper.js';
import { jsx } from 'react/jsx-runtime';
import { Option } from '../select/Option.js';
import { Select } from '../select/Select.js';
import { Input } from '../input/Input.js';
import { Enter, Esc } from '../../utils/keycode.js';

const IMPOSSIBLE_VALUE = `Autocomplete_${Math.random()}`;
const AutoComplete = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    theme,
    children,
    data,
    isAutocompleteEnabled,
    defaultValue,
    value: propValue,
    placeholder,
    label,
    error,
    disabled,
    strict,
    allowClear,
    loading,
    defaultActiveFirstOption = true,
    triggerElement = /*#__PURE__*/jsx(Input, {
      label: label,
      name: label,
      placeholder: placeholder
    }),
    virtualListProps,
    onFocus,
    onBlur,
    onChange,
    onSearch,
    onSelect,
    onPressEnter,
    inputProps
  } = props;
  const [value, setValue] = useMergeValue('', {
    defaultValue,
    value: propValue
  });
  const refInput = useRef(null);
  const refSelect = useRef(null);
  const filterOption = 'filterOption' in props ? props.filterOption : (inputValue, option) => {
    if (strict) {
      return option.props.value.indexOf(inputValue) > -1;
    }
    return option.props.value.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;
  };
  const childNodes = React.Children.toArray(children);
  let selectChildren = null;
  if (childNodes.length && (isSelectOption(childNodes[0]) || isSelectOptGroup(childNodes[0]))) {
    selectChildren = children;
  } else if (data && data.length) {
    selectChildren = data.map((item, index) => {
      if ( /*#__PURE__*/isValidElement(item)) {
        return item;
      }
      if (typeof item === 'string') {
        return /*#__PURE__*/jsx(Option, {
          value: item,
          label: item,
          children: item
        }, index);
      }
      if (typeof item === 'object') {
        const {
          value,
          name
        } = item;
        return /*#__PURE__*/jsx(Option, {
          value: value,
          label: name,
          children: name
        }, index);
      }
      return null;
    });
  }
  useImperativeHandle(ref, () => refInput.current);
  const mergedTrigger = triggerElement || /*#__PURE__*/jsx(Input, {
    label: "trigger",
    name: "merge-trigger"
  });
  const TriggerElement = /*#__PURE__*/React.cloneElement(mergedTrigger, {
    ref: node => {
      refInput.current = node;
      // @ts-ignore
      const {
        ref: originRef
      } = triggerElement;
      if (typeof originRef === 'function') {
        originRef(node);
      }
    },
    value,
    placeholder,
    error,
    disabled,
    allowClear,
    ...inputProps,
    autoComplete: isAutocompleteEnabled ? 'on' : 'off',
    // Empty tag to ensure the consistency of the dom structure of input, such that input won't accidentally lose focus due to structure change on input.
    suffix: loading ? /*#__PURE__*/jsx(Loader, {}) : inputProps?.suffix || /*#__PURE__*/jsx("i", {}),
    onFocus: event => {
      onFocus && onFocus(event);
      inputProps && inputProps.onFocus && inputProps.onFocus(event);
    },
    onBlur: event => {
      onBlur && onBlur(event);
      inputProps && inputProps.onBlur && inputProps.onBlur(event);
    },
    onKeyDown: event => {
      const keyCode = event.keyCode || event.which;
      refSelect.current && refSelect.current.hotkeyHandler(event);
      if (keyCode === Enter.code && onPressEnter) {
        let activeOption;
        if (refSelect.current) {
          activeOption = refSelect.current.getOptionInfoByValue(refSelect.current.activeOptionValue);
        }
        onPressEnter(event, activeOption);
      }
      if (keyCode === Esc.code) {
        refInput.current && refInput.current.blur && refInput.current.blur();
      }
      inputProps && inputProps.onKeyDown && inputProps.onKeyDown(event);
    },
    onChange: (value, event) => {
      setValue(value);
      onSearch && onSearch(value);
      onChange && onChange(value);
      inputProps && inputProps.onChange && inputProps.onChange(value, event);
    }
  });
  const triggerProps = {
    ...props.triggerProps,
    trigger: 'click'
  };
  const selectProps = {
    triggerElement: TriggerElement,
    // Guarantee that onChange can always be triggered
    value: IMPOSSIBLE_VALUE,
    theme,
    inputValue: value,
    defaultActiveFirstOption,
    triggerProps,
    filterOption,
    virtualListProps,
    notFoundContent: null,
    onChange: (value, option) => {
      setValue(value);
      onChange && onChange(value, option);
      value && onSelect && onSelect(value, option);
      // Blur the input on option change
      refInput.current && refInput.current.blur && refInput.current.blur();
    }
  };
  return /*#__PURE__*/jsx(Select, {
    ref: refSelect,
    ...selectProps,
    triggerProps: {
      useAnchorWidth: true
    },
    children: selectChildren
  });
});
AutoComplete.displayName = 'AutoComplete';

export { AutoComplete };
